import { Container } from 'pixi.js';
import { EventEmitter } from '@angular/core';
import { ResourceData } from '../../../../../elements/resource/resource.types';
import {
  AnimationItem,
  CanvasOrientation,
  Translate,
  PositionAnimation,
  ShapeConfig,
  ShapePosition,
  AnimationKeys,
  FillConfig,
  ContainerShapeDescriptor,
  ShapeSelectParams,
} from '../../../../../elements/resource/types/shape.type';
import { RectangleShape } from '../../base/rectangle/rectangle-shape';
import { GeneralShape } from '../general-shape';
import { ContainerShape } from '../../container/container.shape';
import { Line } from '../../primitive/line-element';
import { Dimension } from '../../../../../services/canvas/canvas.types';
import { ShapeService } from '../../../shape.service';
import { CanvasPosition } from '../../../../store/reducer/editor.reducer';
import { setCanvasPosition } from '../../../../store/editor.actions';
import {
  canvasOrientation,
  selectCurrentBackgroundColor,
  STORYBOARD_TYPE_IRI,
} from '../../../../../projects/project.selector';
import { StoryBoardController } from '../../../../../services/canvas/story-board/story-board-controller';
import { RectangleElement } from '../../primitive/rectangle-element';
import {
  _screenX,
  _screenY,
} from '../../../../../services/canvas/canvas.service';
import { setCurrentAnimationId } from '../../../../animation/store/animation.actions';
import {
  baseDescriptorByIRI,
  selectCanvasBackgroundColor,
} from '../../../../store/selector/editor.selector';
import { RootAnimationFrame } from '../../../../animation/frame/main-animation-frame-object';
import { cloneDeep } from 'lodash';
import { filter } from 'rxjs/operators';
import { setDescriptorValue } from '../../../../store/editor.crud.actions';

export class RootShape<
  T extends ContainerShapeDescriptor = ContainerShapeDescriptor,
> extends ContainerShape<T> {
  rootContainer: Container;
  getType(): string {
    return 'root-shape';
  }
  get mainContainer() {
    return this.rootContainer;
  }
  scene: string;
  get absoluteWidth() {
    return _screenX;
  }
  get absoluteHeight() {
    return _screenY;
  }

  get isRoot() {
    return true;
  }
  get canvasPosition() {
    return {
      x: this.translateX || 0,
      y: this.translateY || 0,
      scale: {
        x: this._scaleX || 1,
        y: this._scaleY || 1,
      },
    };
  }

  get completeResourcData() {
    const resourceData = this.resourceObject;
    resourceData.relationships = {
      shape: this.shapes.map(shape => shape.completeResourceData),
    };
    return resourceData;
  }

  get resourceObject(): ResourceData {
    return {
      IRI: this.IRI,
      literals: {
        label: this.literals.label || 'label',
        descriptor: this.descriptor,
      },
    };
  }

  get editable() {
    return true;
  }

  get shapesToDrag(): GeneralShape[] {
    return this.shapes.reduce((acc, shape) => {
      if (shape.selected || shape.currentlyDragged) {
        acc.push(shape);
      }
      return acc;
    }, []);
  }

  mask: RectangleShape<any>;

  get _scaleX() {
    return this.cs.canvasScale;
  }

  set _scaleX(val: number) {
    this.cs.canvasScale = val;
  }

  get _scaleY() {
    return this.cs.canvasScale;
  }

  set _scaleY(val: number) {
    this.cs.canvasScale = val;
  }
  orientation: CanvasOrientation;
  nextSceneIRI: string;
  constructor(
    service: ShapeService,
    data: ResourceData<T>,
    config?: ShapeConfig,
  ) {
    super(service, data, config);
    // console.log('data', data);
    if (config?.state) {
      this.currentState = config.state;
    }

    this.orientation = this.descriptor.orientation || 'landscape';

    this.contextChangedEvent = new EventEmitter();

    this.cs.keyEventSubscribe('Shift+i', () => {
      this.containerForChildren.removeChildren();

      this._shapes.reverse();
      this.containerForChildren.addChild(
        ...this._shapes.map(shape => shape.mainContainer),
      );
    });

    this.subscriptions.push(
      ...[
        // this.store
        //   .select(selectScenes)
        //   .subscribe(scenes => (this.scenes = scenes)),
        // this.store.select(canvasPosition).subscribe(position => {
        //   if (!position) {
        //     return;
        //   }

        //   console.log('------- root-shape')

        //   const { x, y, scale } = position;

        //   this.translateX = x * this.cs.scaleOffset;
        //   this.translateY = y * this.cs.scaleOffset;
        //   this._scaleX = scale.x * this.cs.scaleOffset;
        //   this._scaleY = scale.y * this.cs.scaleOffset;
        //   this.redraw();
        // }),

        this.store.select(selectCanvasBackgroundColor).subscribe(color => {
          this.currentBgColor = color;
          // console.log('currentBgColor', this.currentBgColor);
          this.setBgColor();
          // if (!color) {
          //   return;
          // }
          // this.setBgColor(color);
        }),
        this.store.select(selectCurrentBackgroundColor).subscribe(color => {
          if (!color) {
            return;
          }
        }),
        this.store
          .select(baseDescriptorByIRI(this.IRI))
          .pipe(filter(val => !!val))
          .subscribe((descriptor: T) => {
            this.baseDescriptor = descriptor;
          }),
        this.store.select(canvasOrientation).subscribe(orientation => {
          if (this.orientation == orientation) {
            return;
          }

          this.orientation = orientation;
          this.cs.initScreen(this);
        }),
      ],
    );

    this.checkShapeIndexes();

    this.animationFrame?.calcAnimationsByStart();
    // This works at the container mode
  }

  sceneIndexMap: Record<string, number> = {};

  playAppearPreview() {
    // -- //
    this.shapes.map(shape => shape._hide());
  }

  playDisappearPreview() {
    // -- //
  }

  initSlideAnimation() {
    // -- // -- //
    //
  }

  resetBaseState() {
    // -- // -- //
    if (this.animationService.animationState == 'started') {
      this.animationService.resetBaseState();
    }

    this.currentBgColor = this.service.resolveColor(
      this.descriptor.svgAttributes?.fill?.color,
    );
    this.setBgColor();

    this.animationService.currentTime = 0;
    super.resetBaseState();
    this.store.dispatch(setCurrentAnimationId({ id: null }));

    // ------------------ //
    this.animationService.selectedFrame = null;
    this.cs.currentAnimationID = null;
    this.service.removeSubtitle();
    this._resetPosition();
    this.shapes.map(shape => shape.updateRectangleController());
  }

  _resetPosition() {
    const [x, y] = this.cs.fitTranslate;
    this.translateX = x;
    this.translateY = y;
    this._scaleX = this.cs.fitScale;
    this._scaleY = this.cs.fitScale;
    this.redraw();
  }

  currentBgColor: string;

  setBgColor() {
    this.rect?.patch({
      fill: this.currentBgColor || '#ededed',
    });
  }

  _resetBaseState() {
    super.resetBaseState();
  }

  shapesInOrder: GeneralShape[];

  applyTranslate(translate: Translate) {
    // console.log('----------- applyTranslate -------------', translate); //
    super.applyTranslate(translate);
  }

  afterInit() {
    // console.log('root-shape > afterInit'); //
    super.afterInit();

    this.setContainerMenuItem();

    setTimeout(() => {
      this.setContainerMenuItem();
    }, 500);
    // -- // -- // -- //
    // if (this.descriptor.simplifiedDescriptor) {
    const animations = this.getAutoAnimations();
    animations.reverse();

    let currentAnimation = animations.shift();
    animations.map(animation => {
      animation.next = cloneDeep(currentAnimation);
      currentAnimation = animation;
    });

    // console.log('currentAnimation', currentAnimation);

    // this.animationFrame = new RootAnimationFrame(this, currentAnimation); // -- //

    // const last = (this.shapes[1] as ContainerShape).getContainerAnimation?.();

    // // if (!last) {
    // //   return;
    // // }

    // // last.next = {
    // //   duration: 0.6,
    // //   targetShape: this.IRI,
    // //   type: 'auto-animation',
    // //   action: 'disappear',
    // // } as AutoAnimationFrame;

    const frame = this.getContainerAnimatioFrame({ complete: false });
    // console.log('root-shape > afterInit > descriptor', this.descriptor);
    if (!frame) {
      return;
    }
    this.animationFrame = new RootAnimationFrame(this, frame);

    // this.animationFrame = new RootAnimationFrame(this, {
    //   id: Math.random().toString(),
    //   duration: 1.5,
    //   targetShape: this.IRI,
    //   type: 'appear',
    //   next: {
    //     id: Math.random().toString(),
    //     type: 'action',
    //     duration: 1,
    //     next: {
    //       id: Math.random().toString(),
    //       type: 'disappear',
    //       next: {
    //         id: Math.random().toString(),
    //         type: 'scene-transition',
    //         duration: 1,
    //       },
    //     },
    //   },
    // });

    this.waitForShapesToLoad();
  }

  loaded = false;
  shapesToBeLoaded: Record<string, boolean> = {};

  async waitForShapesToLoad() {
    await new Promise<void>(resolve => {
      const interval = setInterval(() => {
        if (Object.keys(this.shapesToBeLoaded).length == 0) {
          clearInterval(interval);
          resolve();
        }
      }, 100);
    });
    this.cs.setThumbnailByRootShape(this);
  }

  setShapesInOrder() {
    const shapes = this.shapes.filter(shape => shape.getType() != 'glow-shape');

    const isRow = this.service._isRowSelection(shapes, 2);
    const isColumn = this.service._isColumnSelection(shapes, 2);

    this.shapesInOrder = shapes;
    if (isColumn) {
      this.shapesInOrder = shapes.sort((shape1, shape2) => shape1.y - shape2.y);
    }

    if (isRow) {
      this.shapesInOrder = shapes.sort((shape1, shape2) => shape1.x - shape2.x);
    }

    const duration = 1;
    const delay = 0.7;
  }

  _startBaseAnimation(type: 'appear' | 'disappear', division: number): void {
    this.setShapesInOrder();
    console.log('------- start-base-animation -------', division);
    const sections = this.animationService.getWaterfallByOverlap(
      division,
      this.shapes.length,
      0.5 * division,
    );
    this.shapesInOrder.map((shape, index) => {
      // if (type == 'appear') {
      shape._hide();
      // }
      shape.startBaseAnimation(type, sections[index]);
    });
    // -- // -- //
  }
  incrementBaseAnimation(increment: number) {
    this.shapesInOrder.map(shape => shape.incrementBaseAnimation(increment));
  }

  endBaseAnimation() {
    this.shapesInOrder.map(shape => shape.endBaseAnimation());
  }

  removeChildren() {
    this.shapes.map(shape => shape.remove());
    // -- // this.containerForChildren.removeChildren(); // -- // -- //
  }

  storyboardController: StoryBoardController;
  initStoryBoard() {
    if (this.resourceData.type == STORYBOARD_TYPE_IRI) {
      this.storyboardController = new StoryBoardController(
        this,
        this.descriptor.storyBoard || {
          items: [
            {
              id: 1,
              notes: [],
            },
          ],
        },
      );
    }
  }

  checkShapeIndexes() {
    const lookup = {};
    for (const shape of this._shapes) {
      if (lookup[shape.index]) {
        return this.fixShapeIndexes();
      }
      lookup[shape.index] = true;
    }
  }

  fixShapeIndexes() {
    return;
    this._shapes
      .sort((s1, s2) => s1.index - s2.index)
      .map((shape, index) => {
        shape.index = index;
        shape.save();
      });
  }

  setPreviousState(state: string) {
    const index = this.cs.previewShape.states.findIndex(val => val == state);
    if (index > 0) {
      this.setCurrentState[this.cs.previewShape.states[index - 1]];
    }
  }

  get statePositions() {
    return this.descriptor.statePositions;
  }

  set statePositions(val: Record<string, Dimension>) {
    this.descriptor.statePositions = val;
  }

  showContainerItemRC() {}

  saveStagePosition() {
    if (!this.currentState) {
      return;
    }

    this.statePositions ||= {};
    this.statePositions[this.currentState] = {
      x: this.translateX,
      y: this.translateY,
      scale: this._scaleX,
    };
    this.save();
  }

  setCurrentState(state: string) {
    // this.currentState = state;
    // this.cs.currentFunctionName = state;
    // this.cs.animationIsBeingPlayed = false;
    // console.log('addRemoveByState', this.shapesByIndex.length, state);
    // this.shapesByIndex.map(shape => shape.removeContainersFromParent());
    // this.shapesByIndex.map(shape => shape.addByState(state));
    // this.shapesByIndex.map(shape => shape.showHideByState(state));
    // this.cs.generalEventEmit('route-update', { queryParams: { state } });
    // if (!state) {
    //   return;
    // }
    // // this.setStatePosition(state);
    // this.setAnimationFrameByState(state);
  }

  setStatePosition(state: string) {
    // if (this.statePositions?.[state]) {
    //   const { x, y, scale } = this.statePositions[state];
    //   this.translateX = x;
    //   this.translateY = y;
    //   this._scaleX = scale;
    //   this._scaleY = scale;
    // } else {
    //   this.translateX = 0;
    //   this.translateY = 0;
    //   this._scaleX = 1;
    //   this._scaleY = 1;
    // }
    // this.redraw();
  }

  setAnimationFrameByState(state: string) {
    for (const frame of this.animationFrame.getFrames() || []) {
      if (frame.stateTransition) {
        const { to } = frame.stateTransition;
        if (to == state) {
          this.cs.currentAnimationFrame = frame;
          frame.select({ noStateChangeEvent: true });
          // this.shapes
          //   .filter(shape => !shape.if || shape.if == to)
          //   .map(shape => {
          //     const shown = this.animationFrame.getShowHideTill(
          //       shape,
          //       frame.id
          //     );
          //     shown ? shape._show() : shape._hide();
          //   });
          return;
        }
      }
    }
    this.cs.currentAnimationFrame?.deselect();
  }

  deselect(params: ShapeSelectParams = {}) {
    super.deselect(params);
    console.log('root-shape > deselect', params);
    const { onHover } = params;
    if (onHover) {
      return;
    }

    // this.shapes.map(shape => shape.rc?.show());
    // this.rc?.hide(); // -- // -- //

    this.shapes.map(shape => shape.hideContainerItemRC(true));
  }

  select(params?: ShapeSelectParams): void {
    super.select(params);

    if (params?.onHover) {
      return;
    }

    // if (this.shapes.length == 1) {
    //   const [shape] = this.shapes;
    //   shape.select(params);
    // }
    console.log('root-shape > select');
    this.rc?.show({ withoutPCs: true });
    this.rc.hidePointControllers();
  }

  clicked() {
    console.log('root-shape-clicked');
  }

  shiftStateToRight() {
    if (!this.states.length) {
      return;
    }
    let currentIndex = this.states.indexOf(this.currentState);
    currentIndex++;
    if (currentIndex == this.states.length) {
      currentIndex = 0;
    }
    this.setCurrentState(this.states[currentIndex]);
  }

  shiftStateToLeft() {
    if (!this.states.length) {
      return;
    }
    let currentIndex = this.states.indexOf(this.currentState);
    currentIndex--;
    if (currentIndex == -1) {
      currentIndex = this.states.length - 1;
    }
    this.setCurrentState(this.states[currentIndex]);
  }

  addContainersToParent(): void {
    // -- //
  }

  init() {
    if (!this.cellAlignment) {
      this.alignRootShape();
    }
    // -- //
    super.init();
    this.setWidthHeight();
    this.redraw();
  }

  alignRootShape() {
    // -- //
    const { margin } = this.descriptor;
    const [w, h] = [this.cs._canvasWidth, this.cs._canvasHeight];

    const [marginX, marginY] = margin ? [margin, margin] : [w * 0.1, h * 0.1];
    // -- // -- //

    this.containerDims = [w - 2 * marginX, h - 2 * marginY];

    this.cellAlignment = {
      x: marginX,
      y: marginY,
      w: w - 2 * marginX,
      h: h - 2 * marginY,
      align: 'center',
    };

    // console.log('alignRootShape', this.cellAlignment); //

    this.store.dispatch(
      setDescriptorValue({
        IRI: this.IRI,
        key: 'cellAlignment',
        value: this.cellAlignment,
        direct: true,
      }),
    );

    const { x, y } = this.cellAlignment;
    this.translate.x = x;
    this.translate.y = y;
  }

  set opacityy(val: number) {
    this.container.alpha = val;
  }

  rect: RectangleElement;
  maskRect: RectangleElement;

  initContainers() {
    // super.initContainers();
    // this.outerContainer = new Container(); // -- //

    this.rootContainer = new Container();
    this.container = new Container();
    this.auxCircleContainer = new Container();
    this.circleContainer = new Container();
    this.sectionContainer = new Container();

    // this.outerContainer.addChild(this.rootContainer);
    this.rootContainer.addChild(this.container);

    // new RectangleElement(this, this.container, {
    //   width: _screenX,
    //   height: _screenY,
    //   position: { x: 0, y: 0 },
    // });

    this.rootContainer.addChild(this.sectionContainer);
    this.rootContainer.addChild(this.circleContainer);
    this.rootContainer.addChild(this.auxCircleContainer);

    this.rect = new RectangleElement(
      this,
      this.rootContainer,
      {
        width: _screenX,
        height: _screenY,
        position: {
          x: 0,
          y: 0,
        },
        stroke: '#9d9d9d',
        // fill: '#4f6981',
        fill: this.currentBgColor || '#ffffff',
        r: 10,
        'stroke-width': 1.5 / this.cs.canvasScale,
      },
      0,
    )
      // .mouseover(() => {
      //   this.setContainerMenuItem();
      // })
      .mouseout(() => {
        if (!this.selected) {
          this.clearContainerMenuItem();
        }
      });

    // this.maskRect = new RectangleElement(
    //   this,
    //   this.rootContainer,
    //   {
    //     width: _screenX,
    //     height: _screenY,
    //     position: {
    //       x: 0,
    //       y: 0,
    //     },
    //     stroke: '#9d9d9d',
    //     'stroke-opacity': 0.01,
    //     // fill: '#4f6981',
    //     r: 10,
    //     'stroke-width': 1.5 / this.cs.canvasScale,
    //   },
    //   0,
    // );
  }

  applyAnimationById(id: string): void {
    super.applyAnimationById(id);
    this.shapes.map(shape => shape.applyAnimationById(id));
  }

  resetBaseStateByKey(key: AnimationKeys): void {
    switch (key) {
      case 'fill':
        this.currentBgColor = this.descriptor.svgAttributes?.fill?.color;
        this.setBgColor();
        break;
    }
  }

  applyAnimationItem(key: AnimationKeys, item: AnimationItem): void {
    super.applyAnimationItem(key, item);
    switch (key) {
      case 'fill':
        const { color } = item.value as FillConfig;
        this.currentBgColor = this.service.resolveColor(color);
        this.setBgColor();
        break;
    }
    // this.shapes.map(shape => shape.applyAnimationItem(key, item)); //
  }

  // applyAnimationByTime(time: number, direct?: boolean) {
  //   // -- // console.log('apply-animation-by-time', time); // -- // -- //
  //   const bulkData = {};

  //   this.shapes.map(shape => {
  //     const updates = shape.applyAnimationByTime(time, direct);
  //     if (Object.keys(updates || {}).length) {
  //       bulkData[shape.IRI] = updates;
  //     }
  //   });

  //   console.log('applyAnimationByTime', bulkData);
  //   // -- // -- // -- //
  //   if (Object.keys(bulkData).length) {
  //     this.store.dispatch(bulkUpdateFinish());
  //   }
  //   return {};
  // }

  fixIndexes() {
    const shapes = this.shapesByIndex;

    // shapes.map((shape, index) => {
    //   if (isNaN(+shape.index)) {
    //     shape
    //   }
    // })
  }

  editInputs() {
    // TODO - migrate
    // this.cs.openKeyValueEditor(this.descriptor.inputs, data => {
    //   this.descriptor.inputs = data as TypeDef;
    //   this.save();
    // });
  }

  maskShown = false;

  drag(dx: number, dy: number) {
    const [x0, y0] = this.dragBase;
    this.translateX = x0 + dx;
    this.translateY = y0 + dy;
    this.redraw();
  }

  dTransformIncrement(increment: number) {
    const { x, y, scale } = this.dTranslate;

    if (scale) {
      this._scaleX += increment * (scale.x as number);
      this._scaleY += increment * (scale.y as number);
    }

    this.translateX += increment * x;
    this.translateY += increment * y;

    this.redraw();

    // this.dTransform.localIncrement += increment; //
    // this.drag(
    //   this.dTransform.localIncrement * x,
    //   this.dTransform.localIncrement * y,
    //   this.IRI,
    //   'translate'
    // );
  }

  prepareTranslateAnimation(value: Translate, division: number) {
    const { x, y } = value || {};
    // const length = Math.sqrt((Math.pow(x, 2) || 0) + (Math.pow(y, 2) || 0));

    this.dTranslate = {
      localIncrement: 0,
      x: -((+x || 0) / division),
      y: -((+y || 0) / division),
    };
    console.log('dTransform', this.dTranslate);

    this.startBaseDrag();
  }

  prepareTransformAnimation(
    { dx, dy, x, y, offsetX, offsetY, scale }: PositionAnimation,
    division: number,
  ) {
    let _dx: number;
    let _dy: number;

    if (dx || dy) {
      _dx = dx;
      _dy = dy;
    } else {
      _dx = x - this.translateX;
      _dy = y - this.translateY;
    }

    this.dTranslate = {
      localIncrement: 0,
      x: _dx / division,
      y: _dy / division,
      scale: scale
        ? {
            x: ((scale.x as number) - this._scaleX) / division,
            y: ((scale.y as number) - this._scaleY) / division,
          }
        : null,
    };
  }

  prepareKeyValueAnimation(
    animation: AnimationItem,
    division: number,
    duration?: number,
  ): void {
    super.prepareKeyValueAnimation(animation, division, duration);
    const { key, value } = animation;
    switch (key) {
      case 'fill':
        const { color } = value as FillConfig;
        this.currentBgColor = this.service.resolveColor(color);
        this.setBgColor();
        break;

      case 'transform':
        this.prepareTransformAnimation(value as PositionAnimation, division);
        break;
      case 'transform-to-state':
        // -- // -- // -- //
        const to = value as string;
        if (this.statePositions?.[to]) {
          const { x, y, scale } = this.statePositions[to];
          this.prepareTransformAnimation(
            {
              x,
              y,
              scale: {
                x: scale,
                y: scale,
              },
            },
            division,
          );
        }
        break;
    }
  }

  get currentAnimationFrame() {
    return this.animationFrame;
  }

  get currentAnimations() {
    // console.log('root--current-animations', this.animationsById?.[this.cs.currentAnimation?.id]);
    return Object.values(
      this.animationsById?.[this.cs.currentAnimation?.id] || {},
    );
  }

  transformAction({ x, y, scale }: { x: number; y: number; scale: number }) {
    this.translateX += (this.translateX - x) * (scale - 1);
    this.translateY += (this.translateY - y) * (scale - 1);

    // console.log('transform-action', { x, y, scale });
    this._scaleX *= scale;
    this._scaleY *= scale;

    this.service.zoomUpdate();
    this.savePosition();
    this.redraw();
  }

  zoomUpdate() {
    this.rect?.patch({
      'stroke-width': 1.5 / this._scaleX,
    });
  }
  savePosition() {
    this.store.dispatch(
      setCanvasPosition({
        x: this.translateX,
        y: this.translateY,
        scale: { x: this._scaleX, y: this._scaleY },
      }),
    );
  }

  setScale(scale: number) {
    this._scaleX = scale;
    this._scaleY = scale;
    this.redraw();
  }

  setTranslate(x: number, y: number) {
    this.translateX = x;
    this.translateY = y;
    this.redraw();
  }

  translateCanvas(dx: number, dy: number) {
    this.translateX += dx / this._scaleX;
    this.translateY += dy / this._scaleY;

    // console.log('translate-canvas', { dx, dy });
    if (this.cs.currentAnimation && this.cs.zoomMode) {
      this.patch('transform', {
        x: this.translateX,
        y: this.translateY,
        scale: { x: this._scaleX, y: this._scaleY },
      });
    }
    this.savePosition();

    this.redraw();
  }

  redraw() {
    this.rootContainer.setTransform(
      this.translateX,
      this.translateY,
      this._scaleX,
      this._scaleY,
    );
  }

  setResourceData(data: ResourceData) {
    this.resourceData = data as any;
    this.init();
  }

  _redraw({ x, y, scale }: ShapePosition): void {
    this.rootContainer.setTransform(
      x,
      y,
      (scale?.x as number) || 1,
      (scale?.y as number) || 1,
    );
  }

  resetPosition() {
    const position = {
      x: this.cs.translateOffsetX,
      y: 0,
      scale: { x: this.cs.scaleOffset, y: this.cs.scaleOffset },
    };
    this.applyPosition(position);
    this.setPosition(position);
  }

  applyPosition(position: CanvasPosition) {
    const { x, y, scale } = position;
    this.setPosition(position);
    this.rootContainer.setTransform(x, y, scale.x, scale.y);
  }

  setPosition(position: CanvasPosition) {
    if (position.scale?.x == 1.44) {
      // console.log('-- root-shape gotya---', position, this.scene);
    }
    const { x, y, scale } = position;
    this.translateX = x;
    this.translateY = y;
    this._scaleX = scale.x;
    this._scaleY = scale.y;
  }

  save(contextChanged = false) {
    if (contextChanged) {
      this.contextChanged();
    }
  }

  remove() {
    super.remove();
    this.storyboardController?.delete();
  }

  removeAll(): void {
    console.warn('------------- removeAll ----------------');
    this.rootContainer?.removeChildren();
    super.removeAll();
  }

  vAuxLine1: Line;
  vAuxLine2: Line;

  hideAuxLines() {
    this.hideVerticalAuxLines();
    this.hideHorizontalAuxLine();
  }

  hideVerticalAuxLines() {
    this.vAuxLine1?.hide();
    this.vAuxLine2?.hide();
  }

  getAuxLine() {
    return new Line(
      this,
      this.circleContainer,
      {
        position: { x: 0, y: 0 },
        x: 0,
        y: 0,
        ...this.cs.auxLineConfig,
      },
      0,
    );
  }

  drawVerticalAuxLine(x: number, yStart: number, yEnd: number) {
    this.vAuxLine1 ||= this.getAuxLine();
    this.vAuxLine2 ||= this.getAuxLine();

    this.vAuxLine1.patch(
      {
        position: {
          x,
          y: 0,
        },
        x,
        y: yStart,
      },
      true,
    );

    this.vAuxLine2.patch(
      {
        position: {
          x,
          y: yEnd,
        },
        x,
        y: 10_000,
      },
      true,
    );
  }

  hAuxLine1: Line;
  hAuxLine2: Line;

  hideHorizontalAuxLine() {
    this.hAuxLine1?.hide();
    this.hAuxLine2?.hide();
  }

  drawHorizontalAuxLine(y: number, xStart: number, xEnd: number) {
    this.hAuxLine1 ||= this.getAuxLine();
    this.hAuxLine2 ||= this.getAuxLine();

    this.hAuxLine1.patch(
      {
        position: {
          x: 0,
          y,
        },
        x: xStart,
        y,
      },
      true,
    );

    this.hAuxLine2.patch(
      {
        position: {
          x: xEnd,
          y,
        },
        x: 10_000,
        y,
      },
      true,
    );
  }
}
